
import { useState, useEffect, useRef, useCallback } from "react"

export type VoiceRecognitionState = "idle" | "listening" | "processing" | "success" | "error"

export interface VoiceRecognitionResult {
  transcript: string
  confidence: number
}

export interface UseVoiceRecognitionOptions {
  lang?: string
  continuous?: boolean
  interimResults?: boolean
  maxAlternatives?: number
  onResult?: (result: VoiceRecognitionResult) => void
  onError?: (error: string) => void
}

export interface UseVoiceRecognitionReturn {
  state: VoiceRecognitionState
  transcript: string
  isSupported: boolean
  isListening: boolean
  error: string | null
  startListening: () => void
  stopListening: () => void
  forceStop: () => void
  resetTranscript: () => void
}

export function useVoiceRecognition(options: UseVoiceRecognitionOptions = {}): UseVoiceRecognitionReturn {
  const {
    lang = "en-US",
    continuous = false,
    interimResults = true,
    maxAlternatives = 1,
    onResult,
    onError,
  } = options

  const [state, setState] = useState<VoiceRecognitionState>("idle")
  const [transcript, setTranscript] = useState("")
  const [isSupported, setIsSupported] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<any>(null)
  const transcriptRef = useRef("")
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Whisper fallback support
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const useWhisperFallbackRef = useRef(false)
  const mediaStreamRef = useRef<MediaStream | null>(null)

  // Stabilize callbacks with refs to prevent abort on prop changes
  const onResultRef = useRef(onResult)
  useEffect(() => { onResultRef.current = onResult }, [onResult])

  const onErrorRef = useRef(onError)
  useEffect(() => { onErrorRef.current = onError }, [onError])

  // Whisper fallback: Send audio to Whisper API
  const processWhisperAudio = useCallback(async () => {
    console.log('🔄 [WHISPER] Processing audio', {
      timestamp: new Date().toISOString(),
      chunks: audioChunksRef.current.length
    })

    try {
      // Create audio blob
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
      console.log('📦 [WHISPER] Audio blob created', {
        timestamp: new Date().toISOString(),
        size: audioBlob.size,
        sizeInKB: (audioBlob.size / 1024).toFixed(2)
      })

      // Convert to base64
      const reader = new FileReader()
      reader.readAsDataURL(audioBlob)

      reader.onload = async () => {
        try {
          const base64Audio = reader.result as string

          console.log('🚀 [WHISPER] Sending to API', {
            timestamp: new Date().toISOString(),
            base64Length: base64Audio.length
          })

          // Send to Supabase function
          const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) || ""
          const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string) || ""

          const response = await fetch(`${supabaseUrl}/functions/v1/parse-audio`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${supabaseAnonKey}`
            },
            body: JSON.stringify({ audio: base64Audio })
          })

          if (!response.ok) {
            throw new Error(`Whisper API error: ${response.status}`)
          }

          const data = await response.json()
          console.log('✅ [WHISPER] Transcription received', {
            timestamp: new Date().toISOString(),
            transcript: data.transcript
          })

          setTranscript(data.transcript)
          transcriptRef.current = data.transcript

          if (onResultRef.current && data.transcript) {
            onResultRef.current({
              transcript: data.transcript,
              confidence: 1.0 // Whisper doesn't provide confidence
            })
          }

          setState("success")
        } catch (apiError) {
          console.error('❌ [WHISPER] API call failed', {
            timestamp: new Date().toISOString(),
            error: apiError
          })

          const errorMessage = "Failed to transcribe audio. Please try again."
          setError(errorMessage)
          setState("error")
          if (onErrorRef.current) {
            onErrorRef.current(errorMessage)
          }
        }
      }

      reader.onerror = () => {
        console.error('❌ [WHISPER] File reader error')
        setError("Failed to process audio data.")
        setState("error")
      }
    } catch (err) {
      console.error('❌ [WHISPER] Processing error', {
        timestamp: new Date().toISOString(),
        error: err
      })
      setError("Failed to process audio.")
      setState("error")
    }
  }, [])

  // Whisper fallback: Stop recording and process
  const stopWhisperRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('🛑 [WHISPER] Stopping recording', {
        timestamp: new Date().toISOString(),
        state: mediaRecorderRef.current.state
      })
      mediaRecorderRef.current.stop()
    }
  }, [])

  // Whisper fallback: Start audio recording
  const startWhisperRecording = useCallback(async () => {
    console.log('🎙️ [WHISPER] Starting MediaRecorder fallback', {
      timestamp: new Date().toISOString()
    })

    try {
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream

      console.log('✅ [WHISPER] Microphone stream obtained', {
        timestamp: new Date().toISOString(),
        audioTracks: stream.getAudioTracks().length
      })

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      })

      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
          console.log('📦 [WHISPER] Audio chunk received', {
            timestamp: new Date().toISOString(),
            chunkSize: event.data.size,
            totalChunks: audioChunksRef.current.length
          })
        }
      }

      mediaRecorder.onstop = async () => {
        console.log('🛑 [WHISPER] MediaRecorder stopped', {
          timestamp: new Date().toISOString(),
          totalChunks: audioChunksRef.current.length
        })

        // Stop all tracks
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop())
          mediaStreamRef.current = null
        }

        // Process audio
        if (audioChunksRef.current.length > 0) {
          setState("processing")
          await processWhisperAudio()
        } else {
          console.warn('⚠️ [WHISPER] No audio chunks recorded')
          setState("idle")
        }
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(100) // Collect data every 100ms
      setState("listening")
      setError(null)
      useWhisperFallbackRef.current = true

      console.log('✅ [WHISPER] Recording started', {
        timestamp: new Date().toISOString(),
        state: mediaRecorder.state
      })
    } catch (err) {
      console.error('❌ [WHISPER] Failed to start recording', {
        timestamp: new Date().toISOString(),
        error: err
      })

      const errorName = (err as Error).name || 'UnknownError'
      const errorMessage = errorName === 'NotAllowedError'
        ? "Microphone permission denied."
        : "Failed to start audio recording."

      setError(errorMessage)
      setState("error")
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage)
      }
    }
  }, [processWhisperAudio])

  // Check browser support and secure context
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const hasAPI = !!SpeechRecognition
    const isSecure = window.isSecureContext

    // Web Speech API requires secure context (HTTPS or localhost)
    setIsSupported(hasAPI && isSecure)

    if (hasAPI && !isSecure) {
      console.warn('🔒 [VOICE] Secure context required', {
        timestamp: new Date().toISOString(),
        isSecureContext: isSecure,
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        message: 'Voice recognition requires HTTPS or localhost'
      })
    }
  }, [])

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) return

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const recognition = new SpeechRecognition()

    recognition.lang = lang
    recognition.continuous = continuous
    recognition.interimResults = interimResults
    recognition.maxAlternatives = maxAlternatives

    recognition.onstart = () => {
      console.log('🎤 [VOICE] Recognition Started', {
        timestamp: new Date().toISOString(),
        config: { lang, continuous, interimResults, maxAlternatives }
      })
      // Clear any existing silence timer when starting new recognition
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      transcriptRef.current = ""
      setState("listening")
      setError(null)
    }

    recognition.onresult = (event: any) => {
      let finalTranscript = ""
      let interimTranscript = ""

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPiece = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscript += transcriptPiece + " "
        } else {
          interimTranscript += transcriptPiece
        }
      }

      const currentTranscript = (finalTranscript || interimTranscript).trim()
      setTranscript(currentTranscript)
      transcriptRef.current = currentTranscript

      // Clear existing silence timer if user is still talking (interim results)
      if (interimTranscript && !finalTranscript) {
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current)
          silenceTimerRef.current = null
          console.log('🔄 [VOICE] Silence timer cleared (user still speaking)', {
            timestamp: new Date().toISOString(),
            transcript: interimTranscript,
            isFinal: false
          })
        } else {
          console.log('🎤 [VOICE] Interim Result', {
            timestamp: new Date().toISOString(),
            transcript: interimTranscript,
            isFinal: false
          })
        }
      }

      // Start 2-second silence timer when final result available
      if (finalTranscript && onResultRef.current) {
        const confidence = event.results[event.resultIndex][0].confidence
        console.log('🎤 [VOICE] Final Result - Starting 2s Silence Timer', {
          timestamp: new Date().toISOString(),
          transcript: finalTranscript.trim(),
          confidence,
          isFinal: true
        })

        onResultRef.current({
          transcript: finalTranscript.trim(),
          confidence,
        })

        // Start 2-second silence detection timer
        if (!continuous) {
          // Clear any existing timer first
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current)
          }

          silenceTimerRef.current = setTimeout(() => {
            console.log('⏰ [VOICE] 2s Silence Detected - Stopping Recognition', {
              timestamp: new Date().toISOString(),
              finalTranscript: transcriptRef.current
            })
            recognition.stop()
            silenceTimerRef.current = null
          }, 2000) // 2-second silence threshold
        }
      }
    }

    recognition.onerror = async (event: any) => {
      const errorMessage = getErrorMessage(event.error)

      // Enhanced logging for network errors to help with diagnostics
      if (event.error === 'network') {
        console.error('❌ [VOICE] Network Error - Detailed Diagnostics', {
          timestamp: new Date().toISOString(),
          errorType: event.error,
          errorMessage,
          browserInfo: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            onLine: navigator.onLine,
            vendor: navigator.vendor
          },
          securityContext: {
            isSecureContext: window.isSecureContext,
            protocol: window.location.protocol,
            hostname: window.location.hostname
          },
          speechRecognition: {
            lang,
            continuous,
            interimResults,
            maxAlternatives
          },
          troubleshooting: [
            'Automatically falling back to OpenAI Whisper',
            'Check if ad blockers are enabled',
            'Verify firewall allows Google speech API',
            'Try disabling browser extensions',
            'Check network connectivity',
            'Verify microphone is working'
          ]
        })

        // Clear silence timer on error
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current)
          silenceTimerRef.current = null
        }

        // Automatic fallback to Whisper on network error
        console.log('🔄 [VOICE] Automatically falling back to Whisper', {
          timestamp: new Date().toISOString()
        })

        // Don't set error state yet - try Whisper fallback
        setState("idle")
        // Small delay to let Chrome cleanup
        setTimeout(async () => {
          await startWhisperRecording()
        }, 100)
        return
      } else {
        console.error('❌ [VOICE] Recognition Error', {
          timestamp: new Date().toISOString(),
          errorType: event.error,
          errorMessage,
          event
        })
      }

      // Clear silence timer on error
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      setError(errorMessage)
      setState("error")
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage)
      }
    }

    recognition.onend = () => {
      console.log('🎤 [VOICE] Recognition Ended', {
        timestamp: new Date().toISOString(),
        finalTranscript: transcriptRef.current,
      })
      // Clear silence timer when recognition ends
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      setState(prevState => {
        if (prevState === "listening") {
          return transcriptRef.current ? "success" : "idle"
        }
        return prevState
      })
    }

    recognitionRef.current = recognition

    return () => {
      // Clear silence timer on cleanup
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
    }
  }, [isSupported, lang, continuous, interimResults, maxAlternatives])

  const startListening = useCallback(async () => {
    if (!recognitionRef.current || state === "listening") return

    // Check microphone permission first
    console.log('🎤 [VOICE] Checking Microphone Permission', {
      timestamp: new Date().toISOString(),
      state
    })

    try {
      // Request microphone access explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      console.log('✅ [VOICE] Microphone Permission Granted', {
        timestamp: new Date().toISOString(),
        audioTracks: stream.getAudioTracks().length
      })

      // Stop the test stream immediately (we just needed permission check)
      stream.getTracks().forEach(track => track.stop())

      // Now start speech recognition
      recognitionRef.current.start()
    } catch (err) {
      const errorName = (err as Error).name || 'UnknownError'
      const errorMessage = errorName === 'NotAllowedError'
        ? "Microphone permission denied. Please allow microphone access in your browser settings."
        : errorName === 'NotFoundError'
        ? "No microphone found. Please connect a microphone and try again."
        : "Failed to access microphone. Please check your device and try again."

      console.error('❌ [VOICE] Microphone Permission Error', {
        timestamp: new Date().toISOString(),
        errorName,
        error: err
      })

      setError(errorMessage)
      setState("error")
      if (onError) {
        onError(errorMessage)
      }
    }
  }, [state, onError])

  const stopListening = useCallback(() => {
    if (state !== "listening") return

    // Check if using Whisper fallback
    if (useWhisperFallbackRef.current) {
      stopWhisperRecording()
      useWhisperFallbackRef.current = false
      return
    }

    // Otherwise use Chrome Web Speech API
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch (err) {
        // Ignore errors when stopping
      }
    }
  }, [state, stopWhisperRecording])

  const forceStop = useCallback(() => {
    console.log('🛑 [VOICE] Force Stop Called', {
      timestamp: new Date().toISOString(),
      currentState: state,
      isWhisperMode: useWhisperFallbackRef.current
    })

    // Stop Whisper recording if active
    if (useWhisperFallbackRef.current) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        console.log('🛑 [VOICE] Stopping MediaRecorder', {
          timestamp: new Date().toISOString(),
          currentChunks: audioChunksRef.current.length
        })
        mediaRecorderRef.current.stop()
        // Don't clear audioChunksRef here - let onstop handler process them
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
        mediaStreamRef.current = null
      }
      useWhisperFallbackRef.current = false
      // Don't set state to idle - let onstop handler do it
      console.log('✅ [VOICE] Whisper Force Stop Successful', {
        timestamp: new Date().toISOString()
      })
      return
    }

    // Stop Chrome Web Speech API if active
    if (recognitionRef.current) {
      try {
        // Use abort() instead of stop() to immediately terminate without state checks
        recognitionRef.current.abort()
        setState("idle")
        console.log('✅ [VOICE] Chrome Force Stop Successful', {
          timestamp: new Date().toISOString()
        })
      } catch (err) {
        console.error('❌ [VOICE] Chrome Force Stop Failed', {
          timestamp: new Date().toISOString(),
          error: err
        })
      }
    }
  }, [state])

  const resetTranscript = useCallback(() => {
    setTranscript("")
    setError(null)
    setState("idle")
  }, [])

  return {
    state,
    transcript,
    isSupported,
    isListening: state === "listening",
    error,
    startListening,
    stopListening,
    forceStop,
    resetTranscript,
  }
}

function getErrorMessage(error: string): string {
  switch (error) {
    case "no-speech":
      return "No speech detected. Please try again."
    case "audio-capture":
      return "Microphone not available. Please check your device."
    case "not-allowed":
      return "Microphone permission denied. Please allow access in your browser settings."
    case "network":
      // Network error can mean actual network issue OR insecure context
      // Check if we're in a secure context to provide better guidance
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        const hostname = window.location.hostname
        const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(hostname)
        if (isLocalIP) {
          return "Voice input requires HTTPS or localhost. Please use http://localhost:3000 instead of IP address."
        }
        return "Voice input requires a secure connection (HTTPS or localhost)."
      }
      // On HTTPS, network error typically means can't reach Google's speech servers
      return "Voice recognition unavailable. This may be due to ad blockers, firewall restrictions, or network connectivity issues. Please try using the text input field below instead."
    case "aborted":
      return "Voice recognition was cancelled."
    default:
      return "An error occurred during voice recognition."
  }
}
